import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, AlertCircle, Search, X } from 'lucide-react';
import { ParameterDefinition } from '../../types/report';
import { Input } from '../ui';
import { apiService } from '../../services/api';
import useGlobalCustomerStore from '../../stores/globalCustomerStore';

// Get reports service URL
const getReportsApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_REPORT_API_URL;
  const mode = import.meta.env.MODE;

  if (envUrl) {
    return envUrl;
  }

  const API_URLS = {
    local: 'http://localhost:3006/api/v1',
    remote: 'https://ath-avi-reporting-uat-west.azurewebsites.net/api/v1'
  };

  return mode === 'remote' ? API_URLS.remote : API_URLS.local;
};

// Get CRO service URL
const getCroApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_CRO_API_URL;
  const mode = import.meta.env.MODE;

  if (envUrl) {
    return envUrl;
  }

  const API_URLS = {
    local: 'http://localhost:3007/api/v1',
    remote: 'https://ath-avi-cro-uat-west.azurewebsites.net/api/v1'
  };

  return mode === 'remote' ? API_URLS.remote : API_URLS.local;
};

interface DynamicParameterFieldProps {
  paramKey: string;
  definition: ParameterDefinition;
  value: any;
  onChange: (value: any) => void;
  disabled?: boolean;
}

const DynamicParameterField: React.FC<DynamicParameterFieldProps> = ({
  paramKey,
  definition,
  value,
  onChange,
  disabled = false
}) => {
  const { selectedCustomer } = useGlobalCustomerStore();
  const [options, setOptions] = useState<Array<{label: string, value: any}>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Local search text for the searchableMultiselect control
  const [searchText, setSearchText] = useState('');

  // Load options for select/multiselect from API
  useEffect(() => {
    const loadOptions = async () => {
      if (!definition.dataSource) return;

      const { dataSource } = definition;

      // Handle static options
      if (dataSource.type === 'static' && dataSource.options) {
        setOptions(dataSource.options);
        return;
      }

      // Handle API options
      if (dataSource.type === 'api' && dataSource.endpoint) {
        if (!selectedCustomer) {
          setOptions([]);
          return;
        }

        try {
          setLoading(true);
          setError(null);

          // Build query params
          const params = new URLSearchParams({
            customerId: selectedCustomer.id,
            ...dataSource.params
          });

          const endpoint = `${dataSource.endpoint}?${params.toString()}`;

          // Use appropriate service based on serviceType
          let response: any;
          if (dataSource.serviceType === 'reports' || dataSource.serviceType === 'cro') {
            const baseUrl = dataSource.serviceType === 'cro' ? getCroApiUrl() : getReportsApiUrl();
            const token = localStorage.getItem('auth-token');
            const headers: Record<string, string> = {
              'Content-Type': 'application/json'
            };
            if (token) {
              headers['Authorization'] = `Bearer ${token}`;
            }

            const fetchResponse = await fetch(`${baseUrl}${endpoint}`, {
              method: 'GET',
              headers,
              credentials: 'include'
            });

            if (!fetchResponse.ok) {
              throw new Error(`HTTP ${fetchResponse.status}`);
            }

            response = await fetchResponse.json();
          } else {
            response = await apiService.get<any>(endpoint);
          }

          // Navigate to data using responseDataPath (e.g., 'data.partners')
          let data = response;
          if (dataSource.responseDataPath) {
            const pathParts = dataSource.responseDataPath.split('.');
            for (const part of pathParts) {
              data = data[part];
            }
          }

          // Transform data to options
          if (Array.isArray(data)) {
            const transformedOptions = data.map(item => ({
              label: dataSource.labelField ? item[dataSource.labelField] : item.name || item.label,
              value: dataSource.valueField ? item[dataSource.valueField] : item.id || item.value
            }));
            setOptions(transformedOptions);
          } else {
            setError('Invalid response format');
          }
        } catch (err) {
          console.error(`Failed to load options for ${paramKey}:`, err);
          setError(err instanceof Error ? err.message : 'Failed to load options');
        } finally {
          setLoading(false);
        }
      }
    };

    if (
      definition.type === 'select' ||
      definition.type === 'multiselect' ||
      definition.type === 'searchableMultiselect'
    ) {
      loadOptions();
    }
  }, [definition, paramKey, selectedCustomer]);

  // Filtered options for the searchable multiselect (case-insensitive label match)
  const filteredOptions = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o.label).toLowerCase().includes(q));
  }, [options, searchText]);

  // Render different field types
  const renderField = () => {
    switch (definition.type) {
      case 'select':
        return (
          <div>
            <select
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled || loading}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
            >
              <option value="">
                {loading ? 'Loading...' : `Select ${definition.label}`}
              </option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {loading && (
              <div className="flex items-center mt-1 text-xs text-gray-500">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Loading options...
              </div>
            )}
            {error && (
              <div className="flex items-center mt-1 text-xs text-red-600">
                <AlertCircle className="h-3 w-3 mr-1" />
                {error}
              </div>
            )}
          </div>
        );

      case 'multiselect':
        return (
          <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
            {loading ? (
              <div className="flex items-center justify-center py-4 text-xs text-gray-500">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading options...
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-4 text-xs text-red-600">
                <AlertCircle className="h-4 w-4 mr-2" />
                {error}
              </div>
            ) : options.length === 0 ? (
              <div className="text-xs text-gray-500 text-center py-4">
                No options available
              </div>
            ) : (
              options.map((option) => (
                <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={Array.isArray(value) && value.includes(option.value)}
                    onChange={(e) => {
                      const currentValue = Array.isArray(value) ? value : [];
                      if (e.target.checked) {
                        onChange([...currentValue, option.value]);
                      } else {
                        onChange(currentValue.filter(v => v !== option.value));
                      }
                    }}
                    disabled={disabled}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
                  />
                  <span className="text-xs text-gray-700">{option.label}</span>
                </label>
              ))
            )}
          </div>
        );

      case 'searchableMultiselect': {
        const selected: any[] = Array.isArray(value) ? value : [];
        const selectedOptions = options.filter((o) => selected.includes(o.value));
        const maxSelections = definition.maxSelections;
        const atCap = maxSelections !== undefined && selected.length >= maxSelections;
        const toggle = (optionValue: any, checked: boolean) => {
          const current = Array.isArray(value) ? value : [];
          if (checked) {
            // Enforce the cap: ignore adds beyond maxSelections
            if (maxSelections !== undefined && current.length >= maxSelections) return;
            onChange([...current, optionValue]);
          } else {
            onChange(current.filter((v) => v !== optionValue));
          }
        };
        const allFilteredSelected =
          filteredOptions.length > 0 &&
          filteredOptions.every((o) => selected.includes(o.value));
        return (
          <div>
            {/* Selected chips */}
            {selectedOptions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {selectedOptions.map((option) => (
                  <span
                    key={option.value}
                    className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-xs text-blue-700"
                  >
                    {option.label}
                    <button
                      type="button"
                      onClick={() => toggle(option.value, false)}
                      disabled={disabled}
                      className="rounded-full hover:bg-blue-100 p-0.5 disabled:opacity-50"
                      aria-label={`Remove ${option.label}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={() => onChange([])}
                  disabled={disabled}
                  className="text-xs text-gray-500 hover:text-gray-700 underline disabled:opacity-50"
                >
                  Clear all
                </button>
              </div>
            )}

            {/* Search input */}
            <div className="relative mb-1">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={definition.placeholder || `Search ${definition.label.toLowerCase()}...`}
                disabled={disabled || loading}
                className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
              />
            </div>

            {/* Filtered checkbox list */}
            <div className="space-y-1.5 max-h-52 overflow-y-auto border border-gray-200 rounded-lg p-2">
              {loading ? (
                <div className="flex items-center justify-center py-4 text-xs text-gray-500">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Loading options...
                </div>
              ) : error ? (
                <div className="flex items-center justify-center py-4 text-xs text-red-600">
                  <AlertCircle className="h-4 w-4 mr-2" />
                  {error}
                </div>
              ) : options.length === 0 ? (
                <div className="text-xs text-gray-500 text-center py-4">
                  No options available
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="text-xs text-gray-500 text-center py-4">
                  No matches for "{searchText}"
                </div>
              ) : (
                <>
                  {/* Select all (filtered) helper — cap-aware: only fills up to remaining capacity */}
                  <label className="flex items-center space-x-2 cursor-pointer pb-1 mb-1 border-b border-gray-100">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={(e) => {
                        const current = Array.isArray(value) ? value : [];
                        if (e.target.checked) {
                          const toAdd = filteredOptions
                            .map((o) => o.value)
                            .filter((v) => !current.includes(v));
                          const capped =
                            maxSelections !== undefined
                              ? toAdd.slice(0, Math.max(0, maxSelections - current.length))
                              : toAdd;
                          onChange([...current, ...capped]);
                        } else {
                          const filteredVals = new Set(filteredOptions.map((o) => o.value));
                          onChange(current.filter((v) => !filteredVals.has(v)));
                        }
                      }}
                      disabled={disabled || (atCap && !allFilteredSelected)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
                    />
                    <span className="text-xs font-medium text-gray-600">
                      {searchText ? 'Select all matches' : 'Select all'}
                    </span>
                  </label>
                  {filteredOptions.map((option) => {
                    const isChecked = selected.includes(option.value);
                    return (
                      <label
                        key={option.value}
                        className={`flex items-center space-x-2 ${atCap && !isChecked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => toggle(option.value, e.target.checked)}
                          disabled={disabled || (atCap && !isChecked)}
                          className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
                        />
                        <span className="text-xs text-gray-700">{option.label}</span>
                      </label>
                    );
                  })}
                </>
              )}
            </div>
            <div className="flex items-center justify-between mt-1">
              {(selectedOptions.length > 0 || maxSelections !== undefined) && (
                <p className="text-xs text-gray-500">
                  {selectedOptions.length}
                  {maxSelections !== undefined ? ` of ${maxSelections}` : ''} selected
                </p>
              )}
              {atCap && (
                <p className="text-xs text-amber-600">
                  Maximum reached — remove one to add another
                </p>
              )}
            </div>
          </div>
        );
      }

      case 'text':
      case 'number':
        return (
          <Input
            type={definition.type}
            value={value || ''}
            onChange={(e) => onChange(definition.type === 'number' ? parseFloat(e.target.value) : e.target.value)}
            placeholder={definition.placeholder}
            disabled={disabled}
            className="w-full text-xs"
            min={definition.validation?.min}
            max={definition.validation?.max}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={definition.placeholder}
            disabled={disabled}
            rows={3}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
          />
        );

      case 'boolean':
        return (
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
            />
            <span className="text-xs text-gray-700">{definition.label}</span>
          </label>
        );

      case 'date':
        return (
          <Input
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className="w-full text-xs"
            min={definition.validation?.min}
            max={definition.validation?.max}
          />
        );

      case 'dateRange':
        return (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
              <Input
                type="date"
                value={value?.start || ''}
                onChange={(e) => onChange({ ...value, start: e.target.value })}
                disabled={disabled}
                className="w-full text-xs"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
              <Input
                type="date"
                value={value?.end || ''}
                onChange={(e) => onChange({ ...value, end: e.target.value })}
                disabled={disabled}
                className="w-full text-xs"
              />
            </div>
          </div>
        );

      default:
        return (
          <div className="text-xs text-red-600">
            Unsupported field type: {definition.type}
          </div>
        );
    }
  };

  return (
    <div>
      {definition.type !== 'boolean' && (
        <label className="block text-xs font-medium text-gray-700 mb-2">
          {definition.label}
          {definition.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      {renderField()}
      {definition.description && (
        <p className="text-xs text-gray-500 mt-1">{definition.description}</p>
      )}
    </div>
  );
};

export default DynamicParameterField;
